import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import common, {
  closeLoading,
  isMobileScreen,
  showLoading,
  setPageTitle,
} from "../common";
import ShopNavigationPanel from "./extra/ShopNavigationPanel";

const BLOGS_PER_PAGE = 5;

function BlogList() {
  const [blogs, setBlogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageBlogs, setPageBlogs] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  setPageTitle("Blog | Hemp, Kratom, CBD & Delta 8 News & Guides");

  useEffect(() => {
    showLoading("Fetching blogs...");
    setLoading(true);
    fetch(
      `https://star-distributors.s3.us-east-1.amazonaws.com/${
        process.env.REACT_APP_IS_PROD === "true" ? "PROD/" : "DEV/"
      }blogs/index.json`
    )
      .then((res) => res.json())
      .then((data) => {
        closeLoading();
        setLoading(false);
        // Filter out index and png slugs
        const filtered = data.filter(
          (b) => b.slug !== "index" && !b.slug?.includes("png")
        );
        setBlogs(filtered);
        setTotalPages(Math.ceil(filtered.length / BLOGS_PER_PAGE));
      })
      .catch((error) => {
        closeLoading();
        setLoading(false);
        console.error("Error fetching blogs:", error);
      });
  }, []);

  useEffect(() => {
    // Get blogs for current page
    const startIdx = (currentPage - 1) * BLOGS_PER_PAGE;
    const endIdx = startIdx + BLOGS_PER_PAGE;
    const blogsForPage = blogs.slice(startIdx, endIdx);
    // Fetch images only for current page blogs
    async function fetchImages() {
      const updated = await Promise.all(
        blogsForPage.map(async (blog) => {
          try {
            const res = await fetch(
              `https://star-distributors.s3.us-east-1.amazonaws.com/${
                process.env.REACT_APP_IS_PROD === "true" ? "PROD" : "DEV"
              }/blogs/${blog.slug}.png`
            );
            const blob = await res.blob();
            if (blob.size <= 1024) {
              return { ...blog, imageSrc: null };
            }
            const url = URL.createObjectURL(blob);
            return { ...blog, imageSrc: url };
          } catch (error) {
            console.error("Error fetching blog image:", error);
            return { ...blog, imageSrc: null };
          }
        })
      );
      setPageBlogs(updated);
    }
    if (blogsForPage.length > 0) {
      fetchImages();
    } else {
      setPageBlogs([]);
    }
  }, [blogs, currentPage]);

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
    window.scrollTo(0, 0);
  };
  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <section
        className="breadcrumbs-custom"
        data-preset='{"title":"Breadcrumbs","category":"header","reload":false,"id":"breadcrumbs"}'
      >
        <div className="breadcrumbs-custom-container">
          <ul className="breadcrumbs-custom-path">
            <li>
              <a href="/">Home</a>
            </li>
            <li>
              <a>Blogs</a>
            </li>
          </ul>
        </div>
      </section>
      <section
        className={isMobileScreen() ? "bg-white" : "section-sm bg-white"}
      >
        <div className="container-xl">
          <div className="row row-40">
            {isMobileScreen() ? (
              <></>
            ) : (
              <div className="col-md-2">
                <ShopNavigationPanel />
              </div>
            )}
            <div className="col-md-10">
              {loading ? (
                <p style={{ padding: "1rem", textAlign: "center" }}>
                  Loading blogs...
                </p>
              ) : (
                <>
                  {pageBlogs.map((blog) => (
                    <div key={blog.slug}>
                      {blog.imageSrc && (
                        <div className="mb-3">
                          <img
                            className="bloglist-image"
                            src={blog.imageSrc}
                            alt={blog.title}
                          />
                        </div>
                      )}
                      <div className="blog-card">
                        <h4>{blog.title}</h4>
                        <p>{blog.preview}</p>
                        <button
                          className="btn btn-primary px-2 py-1 mb-4 mt-3"
                          onClick={() => {
                            navigate(`/blog/${blog.slug}`);
                            window.scrollTo(0, 0);
                          }}
                        >
                          Read more &gt;&gt;
                        </button>
                      </div>
                      <hr className="mb-4" />
                    </div>
                  ))}
                  {pageBlogs.length === 0 && (
                    <p
                      style={{
                        padding: "1rem",
                        fontStyle: "italic",
                        textAlign: "center",
                      }}
                    >
                      🚧 Our blog is currently under construction. Check back
                      soon for expert insights on cannabis, CBD, Delta-8, and
                      more — all curated to help you make informed choices. 🌿
                    </p>
                  )}
                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <nav className="pag-classic" aria-label="Page navigation">
                      <ul className="pag-classic-list">
                        <li
                          className={`pag-classic-item pag-classic-item-control ${
                            currentPage <= 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="pag-classic-link"
                            onClick={handlePrev}
                            aria-label="Previous"
                          >
                            <svg
                              className="icon-svg"
                              width="6"
                              height="10"
                              viewBox="0 0 6 10"
                              fill="none"
                            >
                              <path d="M-0.000488061 4.96484L4.53159 0.432764C4.8097 0.154653 5.26061 0.154653 5.53872 0.432764C5.81683 0.710875 5.81683 1.16178 5.53872 1.43989L1.00664 5.97197L-0.000488061 4.96484Z"></path>
                              <path d="M1.00664 3.95771L5.53872 8.48979C5.81683 8.76791 5.81683 9.21881 5.53872 9.49692C5.26061 9.77503 4.8097 9.77503 4.53159 9.49692L-0.000488061 4.96484L1.00664 3.95771Z"></path>
                            </svg>
                            <span>Prev</span>
                          </button>
                        </li>
                        <li className="pag-classic-item active">
                          <span className="pag-classic-link">
                            {currentPage === totalPages
                              ? "Last Page"
                              : currentPage <= 1
                              ? "First Page"
                              : "Page " + currentPage}
                          </span>
                        </li>
                        <li
                          className={`pag-classic-item pag-classic-item-control ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="pag-classic-link"
                            onClick={handleNext}
                            aria-label="Next"
                          >
                            <span>Next</span>
                            <svg
                              className="icon-svg"
                              width="7"
                              height="10"
                              viewBox="0 0 7 10"
                              fill="none"
                            >
                              <path d="M6.0708 4.96484L1.53872 0.432764C1.26061 0.154653 0.809703 0.154653 0.531592 0.432764C0.253481 0.710875 0.253481 1.16178 0.531592 1.43989L5.06367 5.97197L6.0708 4.96484Z"></path>
                              <path d="M5.06367 3.95771L0.531592 8.48979C0.253481 8.76791 0.253481 9.21881 0.531592 9.49692C0.809703 9.77503 1.26061 9.77503 1.53872 9.49692L6.0708 4.96484L5.06367 3.95771Z"></path>
                            </svg>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default BlogList;
